// it's important that this import its first
import "./app/debug/console";
import ReactDOM from "react-dom/client";
import { RouterProvider, createRouter } from "@tanstack/react-router";
import { QueryClientProvider } from "@tanstack/react-query";
import { TanStackRouterDevtools } from "@tanstack/react-router-devtools";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import * as Sentry from "@sentry/react";
import "@fontsource-variable/noto-sans-arabic";
import { routeTree } from "./routeTree.gen";
import "./styles.css";
import { isProduction } from "./app/lib/constants";
import { queryClient } from "./app/lib/query";
import { platformApi } from "./app/lib/platform";
import { Devtools } from "./app/debug/Devtools";
import { RouterWrapper } from "./RouterWrapper";

platformApi.init?.();

// Set up a Router instance
const router = createRouter({
  routeTree,
  context: { queryClient },
  scrollRestoration: true,
  defaultPreload: false,
  defaultOnCatch: (error) => {
    if (!isProduction) {
      console.error(error);
    }
    Sentry.captureException(error);
  },
  InnerWrap: RouterWrapper,
  defaultViewTransition: {
    types: ({ fromLocation, toLocation }) => {
      if (!fromLocation) return false;

      const fromPath = fromLocation.pathname;
      const toPath = toLocation.pathname;

      if (
        ![fromPath, toPath].includes("/flights/departure/list") &&
        ![fromPath, toPath].includes("/flights/return/list")
      )
        return false;

      const fromIndex = fromLocation.state.__TSR_index;
      const toIndex = toLocation.state.__TSR_index;
      const isBack = fromIndex > toIndex;

      return isBack ? ["nav-back"] : ["nav-forward"];
    },
  },
});

if (
  ["production", "uat", "staging"].includes(
    import.meta.env.VITE_PUBLIC_ENVIRONMENT,
  )
) {
  Sentry.init({
    dsn: "https://6527f31665e26b7c439b4ab54545c562@o4505543468974080.ingest.us.sentry.io/4507934321541120",
    environment: import.meta.env.VITE_PUBLIC_ENVIRONMENT,
    integrations: [
      Sentry.tanstackRouterBrowserTracingIntegration(router),
      Sentry.extraErrorDataIntegration(),
      Sentry.replayIntegration({
        maskAllText: false,
        blockAllMedia: false,
        unmask: ["[data-sentry-unmask]"],
        unblock: ["[data-sentry-unblock]"],
        networkDetailAllowUrls: [
          /\/api\/v\d+\/mappings/,
          /\/api\/v\d+\/packages/,
          /\/api\/v\d+\/flights$/, // /api/v{version}/flights
          /\/api\/v\d+\/flights\/search$/, // /api/v{version}/flights/search
          /\/api\/v\d+\/flights\/[^/]+\/availability$/, // /api/v{version}/flights/{offerId}/availability
          /\/api\/v\d+\/hotels$/, // /api/v{version}/hotels
          /\/api\/v\d+\/hotels\/search$/, // /api/v{version}/hotels/search
          /\/api\/v\d+\/hotels\/search\/progress$/, // /api/v{version}/hotels/search/progress
          /\/api\/v\d+\/hotels\/[^/]+\/offers$/, // /api/v{version}/hotels/{hotelId}/offers
          /\/api\/v\d+\/hotels\/[^/]+\/availability$/, // /api/v{version}/hotels/{offerId}/availability
        ],
      }),
    ],
    replaysSessionSampleRate: isProduction ? 0.25 : 1.0,
    replaysOnErrorSampleRate: 1.0,
    tracesSampleRate: 1.0,
    enableLogs: true,
  });
}

// Register things for typesafety
declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

const rootElement = document.getElementById("root")!;

if (!rootElement.innerHTML) {
  const root = ReactDOM.createRoot(rootElement);
  root.render(
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
      {!isProduction ? (
        <div dir="ltr">
          <ReactQueryDevtools initialIsOpen={false} />
          <TanStackRouterDevtools router={router} />
          <Devtools />
        </div>
      ) : null}
    </QueryClientProvider>,
  );
}
